/**
 * PATH: apps/quote/src/components/builder/panels/RowInspectorPanel.tsx
 * PURPOSE: Row inspector content panel (orchestrator)
 * WHY: Embeddable content panel for unified right drawer — deep editing of line item details
 */

import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Layers, Package, Truck, Users, Settings, Sparkles, Link2, HelpCircle, Calculator,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { quoteApi, type QuoteLineItem, type QuoteDetail, type AISuggestion } from '@/lib/quoteApi'

// Sub-modules
import ApplicationTab from './inspector/ApplicationTab'
import MaterialsTab from './inspector/MaterialsTab'
import PlantTab from './inspector/PlantTab'
import SubconTab from './inspector/SubconTab'
import OverridesTab from './inspector/OverridesTab'
import AIRecommendationsTab from './inspector/AIRecommendationsTab'
import ProvenanceTab from './inspector/ProvenanceTab'
import AdvancedTab from './inspector/AdvancedTab'

type InspectorTab =
  | 'application'
  | 'materials'
  | 'plant'
  | 'subcon'
  | 'overrides'
  | 'advanced'
  | 'ai'
  | 'provenance'

interface RowInspectorPanelProps {
  quoteId: string
  // Optional — needed for AdvancedTab to read quote.config.custom_columns
  // and surface the cascade. Existing call sites that don't pass it still work.
  quote?: QuoteDetail | null
  sectionId: string
  lineItem: QuoteLineItem | null
  onLineItemUpdated?: (updated: QuoteLineItem) => void
  onUpdateLineItem?: (sectionId: string, itemId: string, patch: Record<string, unknown>) => void
}

function TabButton({
  tab, currentTab, label, icon: Icon, onClick,
}: {
  tab: InspectorTab; currentTab: InspectorTab; label: string
  icon: React.ComponentType<{ className?: string }>; onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors',
        tab === currentTab
          ? 'bg-indigo-600 text-white'
          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
    </button>
  )
}

function NoItemSelected() {
  return (
    <div className="h-full flex flex-col items-center justify-center p-6 text-center">
      <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-xl mb-4">
        <HelpCircle className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="font-medium text-slate-900 dark:text-white mb-1">No item selected</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs">
        Select a line item in the grid to view and edit its details, materials, and applications.
      </p>
    </div>
  )
}

export default function RowInspectorPanel({
  quoteId, quote, sectionId, lineItem, onLineItemUpdated, onUpdateLineItem,
}: RowInspectorPanelProps) {
  const [activeTab, setActiveTab] = useState<InspectorTab>('application')
  const queryClient = useQueryClient()

  useEffect(() => {
    if (lineItem) setActiveTab('application')
  }, [lineItem?.id])

  const applyApplicationMutation = useMutation({
    mutationFn: (applicationId: string) =>
      quoteApi.applyApplication(quoteId, sectionId, lineItem!.id, applicationId),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['quote', quoteId] })
      onLineItemUpdated?.(updated)
    },
  })

  if (!lineItem) return <NoItemSelected />

  return (
    <div className="h-full flex flex-col">
      {/* Item Header */}
      <div className="flex-shrink-0 px-3 py-2 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950 dark:to-purple-950">
        <p className="font-medium text-slate-900 dark:text-white truncate text-sm">
          {lineItem.description || `Item ${lineItem.item_number}`}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          {lineItem.quantity} {lineItem.unit} • {formatCurrency(lineItem.subtotal || 0)}
        </p>
      </div>

      {/* Sub-tabs */}
      <div className="flex-shrink-0 flex flex-wrap gap-1 p-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
        <TabButton tab="application" currentTab={activeTab} label="Application" icon={Layers} onClick={() => setActiveTab('application')} />
        <TabButton tab="materials" currentTab={activeTab} label="Materials" icon={Package} onClick={() => setActiveTab('materials')} />
        <TabButton tab="plant" currentTab={activeTab} label="Plant" icon={Truck} onClick={() => setActiveTab('plant')} />
        <TabButton tab="subcon" currentTab={activeTab} label="Subcon" icon={Users} onClick={() => setActiveTab('subcon')} />
        <TabButton tab="overrides" currentTab={activeTab} label="Overrides" icon={Settings} onClick={() => setActiveTab('overrides')} />
        <TabButton tab="advanced" currentTab={activeTab} label="Advanced" icon={Calculator} onClick={() => setActiveTab('advanced')} />
        <TabButton tab="ai" currentTab={activeTab} label="AI" icon={Sparkles} onClick={() => setActiveTab('ai')} />
        <TabButton tab="provenance" currentTab={activeTab} label="Source" icon={Link2} onClick={() => setActiveTab('provenance')} />
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'application' && (
          <ApplicationTab quoteId={quoteId} sectionId={sectionId} lineItem={lineItem}
            onApply={(appId) => applyApplicationMutation.mutate(appId)} isApplying={applyApplicationMutation.isPending} />
        )}
        {activeTab === 'materials' && (
          <MaterialsTab quoteId={quoteId} sectionId={sectionId} lineItem={lineItem}
            onMaterialsUpdated={onLineItemUpdated ? () => onLineItemUpdated(lineItem) : undefined} />
        )}
        {activeTab === 'plant' && <PlantTab lineItem={lineItem} />}
        {activeTab === 'subcon' && <SubconTab lineItem={lineItem} />}
        {activeTab === 'overrides' && (
          <OverridesTab
            lineItem={lineItem}
            quote={quote ?? null}
            onUpdate={(patch) => {
              if (onUpdateLineItem && lineItem) {
                onUpdateLineItem(sectionId, lineItem.id, patch as Record<string, unknown>)
              }
            }}
          />
        )}
        {activeTab === 'advanced' && (
          <AdvancedTab
            lineItem={lineItem}
            quote={quote ?? null}
            onUpdate={(patch) => {
              if (onUpdateLineItem && lineItem) {
                onUpdateLineItem(sectionId, lineItem.id, patch as Record<string, unknown>)
              }
            }}
          />
        )}
        {activeTab === 'ai' && (
          <AIRecommendationsTab lineItem={lineItem}
            onApply={(suggestion) => {
              if (onUpdateLineItem && lineItem) {
                const patch: Record<string, unknown> = {}
                if (suggestion.suggested_description) patch.description = suggestion.suggested_description
                if (suggestion.suggested_quantity) patch.quantity = suggestion.suggested_quantity
                if (suggestion.suggested_unit) patch.unit = suggestion.suggested_unit
                if (suggestion.suggested_rate) patch.override_rate = suggestion.suggested_rate
                if (suggestion.suggested_labour_rate) patch.labour_rate = suggestion.suggested_labour_rate
                if (suggestion.suggested_material_cost) patch.override_material_cost = suggestion.suggested_material_cost
                if (suggestion.suggested_productivity) patch.productivity_pmpd = suggestion.suggested_productivity
                if (Object.keys(patch).length > 0) onUpdateLineItem(sectionId, lineItem.id, patch)
              }
            }}
          />
        )}
        {activeTab === 'provenance' && (
          <ProvenanceTab lineItem={lineItem}
            onApplyUpdate={(newPrice) => {
              if (onUpdateLineItem && lineItem) onUpdateLineItem(sectionId, lineItem.id, { override_rate: newPrice })
            }}
          />
        )}
      </div>
    </div>
  )
}
